<script setup lang="ts">
/**
 * One image asset: drop a file or paste a URL. Owns the document's `asset.url` and emits the
 * replacement. The drop area shows only while the field is empty — replacing is Remove then drop.
 */
import type { InputPrimaryEvent } from '@shared/ui/fields'

import { faTrashCan } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { DropZoneArea } from '@shared/ui/other'
import { SkeletonBox } from '@shared/ui/loaders'
import { InputPrimary } from '@shared/ui/fields'
import { BtnIcon } from '@shared/ui/buttons'

import {
  ASSIGNMENT_IMAGE_ACCEPT,
  useAssignmentImageUploader,
} from '../../../model/assignments/image-uploader.hook'

defineOptions({
  name: 'AssignmentImageUploader',
})

const props = withDefaults(defineProps<{
  value?: string
  label?: string
  alt?: string
  required?: boolean
  /** Uploads under the template's `cover/` sub-directory. */
  cover?: boolean
  /** Shown while the template has no row to attach media to. */
  cannotUploadHint?: string
  /**
   * Returns, at upload START, the committer for the uploaded path — instead of `update:value` on
   * completion. An event cannot do this: it is emitted when the response lands, and by then this
   * instance may be reused for another block or unmounted, so the parent would write to the wrong one.
   */
  bindChange?: () => (value: string) => void
}>(), {
  value: '',
  label: '',
  alt: '',
  required: false,
  cover: false,
  cannotUploadHint: 'Save the assignment before uploading images.',
  bindChange: undefined,
})

const emit = defineEmits<{
  'update:value': [value: string]
}>()

const {
  refSrc,
  refHasImage,
  refIsPreviewLoading,
  refIsPreviewReady,
  refIsPreviewBlank,
  handleImageLoad,
  handleImageError,
  refExternalUrl,
  refIsUploading,
  refProgress,
  refError,
  refCanUpload,
  handleFiles,
  handleUrlChange,
  handleRemove,
} = useAssignmentImageUploader({
  value: () => props.value,
  cover: () => props.cover,
  onChange: value => emit('update:value', value),
  bindChange: props.bindChange,
})
</script>

<template>
  <div class="assignment-image-uploader">
    <AppText
      v-if="label"
      name="body2"
      color="slate-700"
    >
      {{ required ? `${label} *` : label }}
    </AppText>

    <div class="assignment-image-uploader__columns">
      <div class="assignment-image-uploader__media">
        <div
          v-if="refHasImage"
          class="assignment-image-uploader__preview"
        >
          <!-- Skeleton while the link is signed AND while the bytes arrive: the `<img>` is in the
               DOM but hidden until it fires `load`, so the placeholder is never followed by a
               half-painted picture. (`load` is always dispatched as a later task, so a cached image
               cannot complete before the listener below is attached.) -->
          <SkeletonBox
            v-if="refIsPreviewLoading"
            class="assignment-image-uploader__thumb assignment-image-uploader__thumb--skeleton"
            label="Loading image preview"
          />
          <img
            v-if="refSrc"
            v-show="refIsPreviewReady"
            class="assignment-image-uploader__thumb"
            :src="refSrc"
            :alt="alt"
            :title="value"
            @load="handleImageLoad"
            @error="handleImageError"
          >
          <!-- Never an `<img>` with an empty `src`, or a broken one: a broken-image icon reads as a
               failure when the real state is "the object is gone" or "not an image". -->
          <div
            v-if="refIsPreviewBlank"
            class="assignment-image-uploader__thumb assignment-image-uploader__thumb--blank"
          >
            <AppText
              name="body2"
              color="slate-500"
            >
              No preview
            </AppText>
          </div>

          <BtnIcon
            :icon="faTrashCan"
            size="small"
            color="red"
            class="assignment-image-uploader__remove"
            title="Remove image"
            :disabled="refIsUploading"
            @click="handleRemove"
          />
        </div>

        <DropZoneArea
          v-if="!refHasImage"
          title="Drag & Drop an image here"
          browse-text="Browse images"
          :accept="ASSIGNMENT_IMAGE_ACCEPT"
          :multiple="false"
          :disabled="refIsUploading || !refCanUpload"
          @files="handleFiles"
        />

        <AppText
          v-if="refIsUploading"
          name="body2"
          color="slate-500"
        >
          Uploading… {{ refProgress }}%
        </AppText>

        <AppText
          v-if="!refCanUpload && !refHasImage"
          name="body2"
          color="slate-500"
        >
          {{ cannotUploadHint }}
        </AppText>

        <AppText
          v-if="refError"
          name="body2"
          color="red-500"
        >
          {{ refError }}
        </AppText>
      </div>

      <div class="assignment-image-uploader__fields">
        <!-- Still here on purpose: an image already hosted elsewhere is a legitimate asset, and it
             is never a key in the signed `media` map, so it falls through and renders as-is
             (contract § 11.4). Empty for an uploaded object, whose path is not hand-editable. -->
        <InputPrimary
          name="imageUrl"
          label="…or paste an image URL"
          optional-label="optional"
          :value="refExternalUrl"
          :disabled="refIsUploading"
          placeholder="https://…"
          @change="(event: InputPrimaryEvent) => handleUrlChange(String(event.value))"
        />

        <slot name="fields" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss" src="./AssignmentImageUploader.scss" />
